import { useEffect, useRef, useState } from "react";
import { showToast } from "../../../components/toastService.jsx";
import { weeklyReadIdentity, subscribeWeeklyReadIdentity } from "../../../services/projectWeeklyReads.js";
import { createCustomOrderSession } from "./updatesCustomOrder.js";

const EMPTY_ORDER = Object.freeze([]);

export function useUpdatesCustomOrder(userKey, authIdentity) {
  const scope = JSON.stringify([authIdentity, userKey]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const session = useRef(null);
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!userKey) return undefined;
    let mounted = true;
    let identityValid = true;
    const unsubscribe = subscribeWeeklyReadIdentity(() => { identityValid = false; });
    const allowed = () => identityValid && currentScope.current === scope && weeklyReadIdentity() === authIdentity;
    const owner = createCustomOrderSession({
      scope, allowed,
      onOrder: (ids) => { if (mounted) setState({ scope, ids }); },
      onSaved: () => { if (mounted) setState((s) => s?.scope === scope ? { ...s, error: null, retry: null } : s); },
      onError: (error, retrySave) => {
        const message = retrySave ? "Custom order was not saved." : "Could not load your Custom order.";
        const retry = retrySave || (() => { void owner.load(); });
        if (mounted) setState((s) => ({ scope, ids: s?.scope === scope ? s.ids : [], error: message, retry }));
        showToast(`${message} ${error?.message || ""}`.trim(), { type: "error",
          action: { label: "Retry", onClick: () => { if (allowed()) retry(); } } });
      },
    });
    session.current = { scope, owner };
    void owner.load();
    const hide = () => { void owner.flush(); };
    window.addEventListener("pagehide", hide);
    return () => {
      mounted = false;
      window.removeEventListener("pagehide", hide);
      // Keep the identity invalidation alive until any navigation flush has
      // settled, including an A → B → A switch while a prior PUT is slow.
      void owner.dispose().finally(unsubscribe);
      if (session.current?.owner === owner) session.current = null;
    };
  }, [scope, userKey, authIdentity]);

  return {
    ids: state?.scope === scope ? state.ids : EMPTY_ORDER,
    error: state?.scope === scope ? state.error : null,
    retry: state?.scope === scope ? state.retry : null,
    reorder: (ids) => { if (session.current?.scope === scope) session.current.owner.reorder(ids); },
  };
}
